// src/pages/TherapistPage/CaseHistory/CaseHistoryTab.js
import React, { useCallback, useEffect, useState } from 'react';
import useCaseHistoryEnabled from '../../../hooks/useCaseHistoryEnabled';
import HistoryPanel from './HistoryPanel';
import ClinicalPanel from './ClinicalPanel';
import MeasuresPanel from './MeasuresPanel';
import SignOffPanel from './SignOffPanel';
import WithdrawalQueue from './WithdrawalQueue';
import { listCases, STATUS_LABEL } from './caseFileApi';
import './CaseHistory.css';

/**
 * The fifth dashboard tab. Everything it needs lives in this folder, so the
 * dashboard itself only gains a tab entry and one line that renders this.
 */
export default function CaseHistoryTab({ subTab, onSubTab }) {
    const { enabled, loading: flagLoading } = useCaseHistoryEnabled();
    const [cases, setCases] = useState([]);
    const [caseId, setCaseId] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(() => listCases()
        .then(({ data }) => {
            setCases(data);
            setCaseId((current) => current || (data[0] ? String(data[0].id) : ''));
            setError('');
        })
        .catch(() => setError('Could not load cases.')), []);

    useEffect(() => { if (enabled) load(); }, [enabled, load]);

    if (flagLoading) return <div className="cf-empty">Loading…</div>;
    if (!enabled) {
        return <div className="cf-note amber">Case History is not switched on for this installation yet.</div>;
    }
    // Not about one child: a withdrawal request is decided for a family, and it
    // outlives their case file — approving it deletes the case this tab would
    // otherwise be asking us to pick. So it renders before the picker, and
    // before the "no cases yet" notice.
    if (subTab === 'withdrawals') return <WithdrawalQueue />;

    if (error) return <div className="cf-note red">{error}</div>;
    if (cases.length === 0) {
        return (
            <div className="cf-note blue">
                No case histories yet. They appear here once an intake worker creates one.
            </div>
        );
    }

    const current = cases.find((c) => String(c.id) === String(caseId));

    return (
        <div>
            <div className="cf-bar">
                <label className="cf-hint">Child</label>
                <select value={caseId} onChange={(e) => setCaseId(e.target.value)}>
                    {cases.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.child_name} · Episode {c.episode_number}
                        </option>
                    ))}
                </select>
                {current && (
                    <>
                        <span className={`cf-status ${current.status}`}>
                            {STATUS_LABEL[current.status] || current.status}
                        </span>
                        <span className="cf-hint">Waiting on: {current.waiting_on}</span>
                    </>
                )}
            </div>

            {subTab === 'measures' && <MeasuresPanel caseId={caseId} />}
            {subTab === 'clinical' && <ClinicalPanel caseId={caseId} />}
            {subTab === 'sign-off' && <SignOffPanel caseId={caseId} onChanged={load} />}
            {subTab !== 'measures' && subTab !== 'clinical' && subTab !== 'sign-off'
                && <HistoryPanel caseId={caseId} onChanged={load} />}
        </div>
    );
}
